export const formatPrice = (value) => {
  if (value === null || value === undefined) return null;

  // Strip commas first
  const cleanedValue =
    typeof value === "string" ? value.replace(/,/g, "") : value;

  const num = Number(cleanedValue);
  if (isNaN(num)) return null;

  const absValue = Math.abs(num);

  // 🔑 Only abbreviate from 6 figures
  if (absValue >= 1.0e12)
    return (num / 1.0e12).toFixed(2).replace(/\.00$/, "") + "T";

  if (absValue >= 1.0e9)
    return (num / 1.0e9).toFixed(2).replace(/\.00$/, "") + "B";

  if (absValue >= 1.0e6)
    return (num / 1.0e6).toFixed(2).replace(/\.00$/, "") + "M";

  if (absValue >= 1.0e5)
    return (num / 1.0e3).toFixed(2).replace(/\.00$/, "") + "K";

  // Below 6 figures → normal number
  return num.toFixed(2).replace(/\.00$/, "");
};

// utils/numberFormat.js

export const formatWithCommasAndDecimals = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const str = value.toString();

  // If user typed a trailing dot (e.g., "13.")
  const endsWithDot = str.endsWith(".");

  const [intPart, decPart] = str.split(".");

  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (endsWithDot) {
    return formattedInt + ".";
  }

  if (!decPart) return formattedInt;

  const trimmedDec = decPart.slice(0, 3);

  return `${formattedInt}.${trimmedDec}`;
};

export const parseRawNumber = (value) => {
  if (value === null || value === undefined) return "";

  // keep trailing dot (e.g. "13.")
  const endsWithDot = value.endsWith(".");

  let raw = value.replace(/,/g, "");

  if (raw.includes(".")) {
    const [i, d] = raw.split(".");
    if (endsWithDot) return `${i}.`; // preserve trailing .
    raw = d ? `${i}.${d.slice(0, 3)}` : i;
  }

  return raw;
};

const CURRENCY_CONFIG = {
  AUD: { symbol: "A$", locale: "en-AU" },
  USD: { symbol: "$", locale: "en-US" },
  EUR: { symbol: "€", locale: "de-DE" },
  GBP: { symbol: "£", locale: "en-GB" },
  JPY: { symbol: "¥", locale: "ja-JP" },
  CAD: { symbol: "C$", locale: "en-CA" },
  INR: { symbol: "₹", locale: "en-IN" },
};

export const formatCurrency = (value, currency = "AUD") => {
  // Resolve config — accept both "AUD" and "A$" style inputs
  const config =
    CURRENCY_CONFIG[currency] ??
    Object.values(CURRENCY_CONFIG).find((c) => c.symbol === currency) ??
    CURRENCY_CONFIG.AUD;

  const { symbol, locale } = config;

  if (value == null || value === "") return `${symbol} 0.00`;

  const cleanedValue =
    typeof value === "string" ? value.replace(/,/g, "") : value;

  const num = Number(cleanedValue);

  if (Number.isNaN(num)) {
    console.warn("Invalid currency value:", value);
    return `${symbol} 0.00`;
  }

  // JPY and similar zero-decimal currencies
  const isZeroDecimal = currency === "JPY";
  const fractionDigits = isZeroDecimal ? 0 : 2;

  if (Math.abs(num) >= 1_000_000) {
    return `${symbol} ${num.toLocaleString(locale, {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}`;
  }

  return `${symbol} ${num.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

